// Import necessary React hooks
import { useState } from "react";
import { useNavigate } from "react-router-dom";
// Import axios utilities
import axios from "axios";
// Import necessary Material-UI components
import {
  Box,
  Button,
  Typography,
  Dialog,
  TextField,
  Grid,
  Container,
  CssBaseline,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
// Import MUI Date utilities and functions
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import { countryList } from "../";
// Import custom validation function, hooks and components
import { validateSampleDetailsInput } from "../validation/validateSampleDetailsInput";
import {
  apiURI,
  authErrorRedirect,
  CustomImagePicker,
  CustomSelect,
  CustomTextfield,
  processImages,
  productClasses,
} from "../../common";

export function SampleRequestSampleControlAddSupplierDetailsModal({
  open,
  setOpen,
  data,
  sampleRequestID,
  sampleItemID,
  supplierValues,
  setReloadData,
}) {
  // Navigation hook for programmatically navigating
  const history = useNavigate();
  // Function to close the modal
  const handleClose = () => {
    setOpen(false);
  };
  // State for storing errors
  const [errors, setErrors] = useState();
  // State for storing primary and secondary product images
  const [primaryProductImages, setPrimaryProductImages] = useState([]);
  const [secondaryProductImages, setSecondaryProductImages] = useState([]);
  // State for storing the selected country of origin
  const [countryOfOriginSelect, setCountryOfOriginSelect] = useState("");
  // Function to handle country of origin selection
  const handleCountryOfOriginSelect = (event, countryName) => {
    setCountryOfOriginSelect(countryName);
  };
  // State for storing boolean for progress of image compression
  const [isCompressing, setIsCompressing] = useState(false);
  // State for storing timer for Secondary Message in CustomBackdrop
  const [patienceTimer, setPatienceTimer] = useState(false);
  // State for storing timer for checking if data is submitting
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for storing form values
  const [values, setValues] = useState({
    brand: "",
    receivedItemDescription: "",
    receivedProductClass: "",
    packaging: "",
    productionDate: "",
    expiryDate: "",
  });

  // Function to handle form value changes
  const onChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  // Function to handle sending sample details data
  async function handleSendData(sampleDetails) {
    setErrors({});

    // Combine primary and secondary product images
    let combinedProductImages = [
      ...primaryProductImages,
      ...secondaryProductImages,
    ];

    // processImages now reliably awaits FileReader completion (getBase64 race condition fixed)
    let combinedImagesToUpload = await processImages(
      combinedProductImages,
      setPatienceTimer,
      setIsCompressing,
    );

    let {
      imagesToUpload,
      primaryImagesToUpload,
      secondaryImagesToUpload,
      ...sampleDataRest
    } = sampleDetails;

    setIsSubmitting(true);

    let sampleData = {
      supplierValues: supplierValues,
      sampleRequestID: sampleRequestID,
      sampleItemID: sampleItemID,
      sampleDetails: {
        ...sampleDataRest,
        primaryProductImages: combinedImagesToUpload.slice(
          0,
          primaryProductImages.length,
        ),
        secondaryProductImages: combinedImagesToUpload.slice(
          primaryProductImages.length,
        ),
      },
    };

    await axios
      .post(`${apiURI}/form/addSampleDetails`, sampleData)
      .then(() => {
        setIsSubmitting(false);
        setReloadData(true);
      })
      .catch((response) => {
        console.log("error in axios form call react");
        setErrors({});
        setErrors(response.response.data);
        authErrorRedirect(response.response.data, history);
      });
  }

  // Function to handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const sampleDetails = {
      ...data,
      receivedItemDescription: values.receivedItemDescription,
      receivedProductClass: values.receivedProductClass,
      brand: values.brand,
      countryOfOrigin: countryOfOriginSelect,
      packaging: values.packaging,
      productionDate: formData.get("productionDate").replace(/ /g, ""),
      expiryDate: formData.get("expiryDate").replace(/ /g, ""),
      receiptDate: formData.get("receiptDate"),
      primaryImagesToUpload: primaryProductImages?.length,
      secondaryImagesToUpload: secondaryProductImages?.length,
    };

    // Validate form data
    const { validationErrors, isValid } =
      validateSampleDetailsInput(sampleDetails);
    !isValid && setErrors(validationErrors);
    isValid && setErrors();
    // isValid && console.log(sampleDetails);
    isValid && handleSendData(sampleDetails);
  };
  errors && console.log({ errors });

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        style={{
          height: "90%",
        }}
        PaperProps={{ sx: { borderRadius: "5px" } }}
      >
        <Box
          sx={{
            display: isCompressing ? "none" : "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Container
            component="main"
            maxWidth="sm"
            sx={{
              marginTop: 2,
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography component="h1" variant="h5">
              Add Sample Details
            </Typography>
            <CssBaseline />
            <Box
              component="form"
              noValidate
              sx={{
                mt: 5,
              }}
              onSubmit={handleSubmit}
            >
              <Grid container spacing={2}>
                <CustomTextfield
                  id="receivedItemDescription"
                  values={values}
                  errors={errors}
                  onChange={onChange}
                  gridValues={{ xs: 12, sm: 12 }}
                />

                {[
                  {
                    name: "receivedProductClass",
                    menuItems: productClasses,
                  },
                ].map((item, index) => (
                  <CustomSelect
                    id={item.name}
                    menuItems={item.menuItems}
                    values={values}
                    errors={errors}
                    onChange={onChange}
                    gridValues={{ xs: 12, sm: 12 }}
                    key={index}
                  />
                ))}
                <CustomTextfield
                  id="brand"
                  values={values}
                  errors={errors}
                  onChange={onChange}
                  gridValues={{ xs: 12, sm: 12 }}
                />
                <Grid item xs={12} sm={12}>
                  <Autocomplete
                    options={countryList}
                    value={countryOfOriginSelect}
                    // labelId="country-of-origin-select-label"
                    id="country-of-origin-select-label"
                    name="countryOfOrigin"
                    onChange={(e, countryName) =>
                      handleCountryOfOriginSelect(e, countryName)
                    }
                    renderInput={(params) => {
                      return (
                        <TextField
                          label="Country Of Origin"
                          {...params}
                          error={errors && errors.countryOfOrigin && true}
                          helperText={
                            errors && errors.countryOfOrigin !== ""
                              ? errors.countryOfOrigin
                              : ""
                          }
                        />
                      );
                    }}
                  />
                </Grid>
                <CustomTextfield
                  id="packaging"
                  values={values}
                  errors={errors}
                  onChange={onChange}
                  gridValues={{ xs: 12, sm: 12 }}
                />
                <Grid item xs={12} sm={12}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: errors && errors.productionDate && true,
                          helperText:
                            errors && errors.productionDate !== ""
                              ? errors.productionDate
                              : "",
                          id: "productionDate",
                          name: "productionDate",
                          readOnly: true,
                        },
                      }}
                      label="Production Date"
                      id="productionDate"
                      name="productionDate"
                      maxDate={moment(Date.now())}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: errors && errors.expiryDate && true,
                          helperText:
                            errors && errors.expiryDate !== ""
                              ? errors.expiryDate
                              : "",
                          id: "expiryDate",
                          name: "expiryDate",
                          readOnly: true,
                        },
                      }}
                      label="Expiry Date"
                      id="expiryDate"
                      name="expiryDate"
                      minDate={moment(Date.now())}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="filled"
                    required
                    fullWidth
                    value={new Date().toDateString()}
                    label="Receipt Date"
                    id="receiptDate"
                    name="receiptDate"
                    error={errors && errors.receiptDate && true}
                    helperText={
                      errors && errors.receiptDate !== ""
                        ? errors.receiptDate
                        : ""
                    }
                  />
                </Grid>

                {/* Custom imagepickers for primary and secondary product images */}
                {[
                  {
                    error: errors?.primaryImagesToUpload,
                    selectedImages: primaryProductImages,
                    setSelectedImages: setPrimaryProductImages,
                    pickerID: "primary packaging",
                  },
                  {
                    error: errors?.secondaryImagesToUpload,
                    selectedImages: secondaryProductImages,
                    setSelectedImages: setSecondaryProductImages,
                    pickerID: "secondary packaging",
                  },
                ].map((item, index) => (
                  <Grid item xs={12} sx={{ mt: "-10px" }} key={index}>
                    <CustomImagePicker
                      error={errors && item.error}
                      selectedImages={item.selectedImages}
                      setSelectedImages={item.setSelectedImages}
                      pickerId={item.pickerID}
                      multiple={true}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    mt: 3,
                    width: "150px",
                    float: "right",
                    mb: 3,
                  }}
                >
                  Save
                </Button>
              </Grid>
            </Box>
          </Container>
        </Box>

        {/* Box to display when images are compressing (hide the forms above while images are compressing) */}
        <Box
          sx={{
            display: isCompressing ? "flex" : "none",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Container
            component="main"
            maxWidth="sm"
            sx={{
              margin: "20px 0px",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Grid item xs={12}>
              <CircularProgress color="inherit" sx={{ marginLeft: "20px" }} />
            </Grid>
            <Grid item xs={12}>
              <h3>Please wait: Compressing selected images</h3>{" "}
            </Grid>
            {/* Displaying a CircularProgress component to indicate loading or processing */}
            <Grid item xs={12}>
              <h3 style={{ display: patienceTimer ? "flex" : "none" }}>
                Larger images take longer to compress.
              </h3>
            </Grid>
          </Container>
        </Box>
      </Dialog>
    </div>
  );
}
